import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, BadgeCheck, FileText } from "lucide-react";
import { motion } from "framer-motion";
import {
  FaShieldAlt, // For Zero Depreciation
  FaTools, // For Engine Protection
  FaFileInvoiceDollar, // For RTI
  FaTruckPickup, // For Roadside Assistance
  FaKey, // For Key Replacement Cover
  FaCloudSunRain,
  FaUserLock,
  FaCarCrash,
  FaCarSide,
} from "react-icons/fa";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${
        id === open ? "rotate-180" : ""
      } h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const NissanCarInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "Is zero depreciation add-on worth it for Nissan Magnite?",
      answer:
        "Yes. Especially in the first few years, it helps avoid part cost deductions during claims.",
    },
    {
      question: "Are turbo variants of Nissan costlier to insure?",
      answer:
        "Slightly yes. Turbocharged models carry a marginally higher premium.",
    },
    {
      question: "Can I get insurance for an older Nissan Micra or Sunny?",
      answer:
        "Yes. While these models are discontinued, insurance is still available based on vehicle condition.",
    },
    {
      question: "Do Nissan cars qualify for cashless repair?",
      answer:
        "Yes, provided your insurer has tie-ups with authorized service centers.",
    },
    {
      question: "Which is the cheapest Nissan model to insure?",
      answer:
        "The base variants of the Nissan Magnite usually have the lowest insurance costs.",
    },
    {
      question: "Is engine protection cover necessary?",
      answer:
        "It’s highly recommended, especially if you drive in areas prone to waterlogging or own a turbo model.",
    },
    {
      question: "Can I transfer my No Claim Bonus to a new Nissan car?",
      answer:
        "Yes, NCB is transferable across vehicles registered under your name.",
    },
    {
      question: "Are accessories like infotainment units covered?",
      answer:
        "Only if declared and insured separately. Undeclared accessories might not be compensated during claims.",
    },
    {
      question: "Does Nissan offer bundled insurance at the time of purchase?",
      answer:
        "Yes, dealerships often offer one to three-year bundled insurance packages.",
    },
    {
      question: "What if I miss my policy renewal?",
      answer:
        "A short grace period is available, but post that, inspection may be required. Renew early to avoid complications.",
    },
    {
      question:
        "Is it legal to drive my Nissan with only third-party insurance?",
      answer:
        "Yes, but it’s not advisable. You won’t be covered for own damage or natural calamities.",
    },
    {
      question: "Are used Nissan cars insurable?",
      answer:
        "Yes, both third-party and comprehensive coverage is available for used Nissan vehicles.",
    },
    {
      question: "Can I cancel my Nissan insurance mid-policy?",
      answer:
        "Yes, but only after proof of alternate insurance or ownership transfer.",
    },
    {
      question: "How long does a Nissan insurance claim take?",
      answer:
        "Usually between 7 to 14 days, depending on damage and documentation.",
    },
    {
      question: "Are electrical components like power windows covered?",
      answer:
        "Yes, under a comprehensive plan or electrical accessory cover if declared.",
    },
    {
      question: "Does fuel type affect Nissan insurance rates?",
      answer:
        "Yes, diesel and turbo-petrol models may carry slightly higher premiums.",
    },
    {
      question: "Can I insure a car with modifications?",
      answer:
        "Only minor modifications may be covered. Major changes need to be declared and approved.",
    },
    {
      question: "Is insurance cheaper if I opt for a smaller engine Nissan?",
      answer:
        "Yes, models with smaller engines and lower IDV generally have cheaper premiums.",
    },
    {
      question: "Do insurers provide add-ons for breakdowns on highways?",
      answer: "Yes. Most offer roadside assistance as an optional cover.",
    },
    {
      question: "What’s the penalty for uninsured Nissan vehicles?",
      answer:
        "Driving without valid insurance can result in a fine of ₹2,000 to ₹4,000 and other penalties.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Zero Depreciation",
      description:
        "Recovers full cost of damaged parts without depreciation deductions.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Engine Protection Cover",
      description:
        "Essential in flood-prone cities to cover engine repair costs.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "Return to Invoice Cover",
      description:
        "Helps you get the full on-road price if the vehicle is stolen or totaled.",
      icon: <FaFileInvoiceDollar className="text-4xl text-orange-600 mb-4" />,
    },
    {
      title: "Roadside Assistance",
      description:
        "Provides help for minor breakdowns, flat tyres or fuel emergencies.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Key Replacement Cover",
      description:
        "Ideal for tech-enabled key fobs used in newer Nissan models.",
      icon: <FaKey className="text-4xl text-green-600 mb-4" />,
    },
  ];

  const features = [
    { icon: "🛡️", text: "Reliable & Accurate Estimates" },
    { icon: "⏱️", text: "Takes Less Than a Minute" },
    { icon: "📉", text: "Instantly Shows Premium Breakup" },
    { icon: "📄", text: "No Paperwork or Documentation" },
    { icon: "📲", text: "Easy to Use on Any Device" },
    { icon: "💰", text: "Helps You Save on Insurance" },
  ];
const nissaninsurance = [
    {
      name: 'Magnite',
      path: '/nissan-magnite-car-insurance',
      image: '/assets/nissanmagnite.jpeg',
    },
       {
      name: 'Kicks',
      path: '/nissan-kicks-car-insurance',
      image: '/assets/nissankicks.jpeg',
    },
      {
      name: 'GTR',
      path: '/nissan-gtr-car-insurance',
      image: '/assets/nissangtr.jpeg',
    },
     {
      name: 'Sunny',
      path: '/nissan-sunny-car-insurance',
      image: '/assets/nissansunny.jpeg',
    },
  ];
  return (
    <>
      <Helmet>
        <title>
          Nissan Car Insurance in India – Compare Quotes & Buy Online
        </title>
        <meta
          name="description"
          content="Explore Nissan car insurance options in India. Compare quotes, customize coverage, and buy online to protect your Nissan vehicle."
        />
        <meta
          name="keywords"
          content="nissan car insurance, auto insurance india, nissan magnite insurance, nissan kicks insurance, car insurance quotes"
        />
         <link rel="canonical" href="https://thefinmaster.com/nissan-car-insurance" />
       <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
       
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">Nissan Car Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">
                    Nissan Car Insurance 
                  </span>{" "}
                 
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                Nissan offers a blend of style, comfort, and performance that appeals to a wide range of drivers in India. Whether you own a sporty Magnite or a versatile Kicks, ensuring your car is well-protected starts with the right insurance plan. This guide covers everything you need to know about insuring a Nissan vehicle, from average premiums and model insights to useful add-ons, renewal tips, and frequently asked questions.

                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/nissaninsurance.jpeg"
                alt="Nissan Car"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center p-4 mt-6">
        <div className="bg-white shadow-lg rounded-2xl p-6 max-w-7xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            About Nissan in India
          </h2>
          <p className="text-gray-600 leading-relaxed">
            Nissan Motor India has been steadily growing in popularity with a
            focused portfolio of stylish, efficient and tech-forward cars. Known
            for strong build quality and striking design, Nissan’s current
            Indian lineup includes compact SUVs and sub-compact hatchbacks.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            Models like the Nissan Magnite and Nissan Kicks appeal to both city
            commuters and adventure seekers. Backed by the brand’s global
            reliability and evolving service network, owning a Nissan makes
            sense for drivers who seek value and modern features.
          </p>
        </div>
      </div>

         <div className="p-4 sm:p-8 bg-white">
                                    <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
                                      Choose Your Car Brand
                                    </h2>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                {nissaninsurance.map((calc, index) => (
                                  <motion.div
                                    key={index}
                                    whileHover={{ scale: 1.04 }}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3, delay: index * 0.05 }}
                                  >
                                    <Link
                                      to={calc.path}
                                      className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
                                    >
                                      <img
                                        src={calc.image}
                                        alt={calc.name}
                                        className="w-16 h-16 object-contain mb-2"
                                      />
                                      <span className="text-md font-medium text-gray-800 break-words leading-snug">
                                        {calc.name}
                                      </span>
                                    </Link>
                                  </motion.div>
                                ))}
                              </div>
                              </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 opacity-20 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-400 opacity-20 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Why Nissan Owners Need the Right Insurance Plan
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            Nissan cars often pack premium features, modern safety technologies
            and well-built exteriors. Having an insurance policy tailored to
            your car and driving style helps shield you from:
          </p>
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-gray-700 mb-6">
            <li className="flex items-start gap-3">
              <FaCarCrash className="text-xl text-red-500 mt-1" />
              <span>Road accidents and repair expenses</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserLock className="text-xl text-yellow-500 mt-1" />
              <span>Fire, vandalism and car theft</span>
            </li>
            <li className="flex items-start gap-3">
              <FaCloudSunRain className="text-xl text-blue-500 mt-1" />
              <span>Natural disasters like floods or earthquakes</span>
            </li>
            <li className="flex items-start gap-3">
              <FaShieldAlt className="text-xl text-green-500 mt-1" />
              <span>Third-party liabilities</span>
            </li>
            <li className="flex items-start gap-3">
              <FaTools className="text-xl text-indigo-500 mt-1" />
              <span>
                Expensive part replacements, especially for turbocharged
                variants
              </span>
            </li>
          </ul>
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-700">
            A good policy also ensures access to authorized service centers,
            cashless repairs and coverage flexibility to suit your driving
            conditions.
          </div>
        </motion.div>
      </section>
      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="max-w-xl space-y-6 text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
              Calculate Your Car Insurance <br />
              in <span className="text-blue-600">Seconds</span>
            </h2>
            <p className="text-gray-700 text-lg">
              Use our fast and free calculator to estimate your premium — no
              paperwork, no waiting.
            </p>
            <Link
              to="/financial-tools-calculators/car-insurance-calculator"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
            >
              Start Calculating <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="space-y-4 text-left">
            {features.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xl" role="img" aria-label={item.text}>
                  {item.icon}
                </span>
                <span className="text-gray-800 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Popular Nissan Models and Insurance Cost Estimates
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Here’s a look at Nissan’s key models currently available in India,
          along with their approximate Insured Declared Value (IDV) and typical
          starting premium range for comprehensive plans:
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              {
                model: "Nissan Magnite",
                category: "Compact SUV",
                idv: "5.5–10 Lakh",
                premium: "₹3,000 to ₹5,800",
              },
              {
                model: "Nissan Kicks",
                category: "Mid-Size SUV",
                idv: "9–13 Lakh",
                premium: "₹4,000 to ₹6,700",
              },
              {
                model: "Nissan GT-R (CBU)",
                category: "Sports/Performance",
                idv: "2.2 Cr+",
                premium: "₹1.5 Lakh and above",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="border mb-4 p-4 rounded-lg bg-white shadow-md"
              >
                <h3 className="font-semibold text-gray-800 mb-2">
                  {item.model}
                </h3>
                <div className="text-gray-600 mb-1">
                  <strong>Category:</strong> {item.category}
                </div>
                <div className="text-gray-600 mb-1">
                  <strong>Approx. IDV:</strong> {item.idv}
                </div>
                <div className="text-gray-600">
                  <strong>Starting Premium:</strong> {item.premium}
                </div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">
                    Model
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Category
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Approx. IDV (₹)
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Starting Premium (₹)
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  [
                    "Nissan Magnite",
                    "Compact SUV",
                    "5.5–10 Lakh",
                    "₹3,000 to ₹5,800",
                  ],
                  [
                    "Nissan Kicks",
                    "Mid-Size SUV",
                    "9–13 Lakh",
                    "₹4,000 to ₹6,700",
                  ],
                  [
                    "Nissan GT-R (CBU)",
                    "Sports/Performance",
                    "2.2 Cr+",
                    "₹1.5 Lakh and above",
                  ],
                ].map(([model, category, idv, premium], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{category}</td>
                    <td className="p-3 border border-gray-300">{idv}</td>
                    <td className="p-3 border border-gray-300">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-gray-600 text-sm md:text-base mt-4 text-center">
            Note: Insurance costs for the GT-R are much higher due to its
            classification as a performance luxury car. Most buyers in India
            focus on the Magnite and Kicks.
          </p>
        </div>
      </div>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Add-Ons That Work Well for Nissan Cars
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Choosing the right add-ons helps you avoid major financial setbacks
          when unexpected events occur. For Nissan vehicles, the following
          add-ons are highly recommended:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="mx-auto container p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Best Insurance Companies for Nissan Cars
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Many general insurers offer Nissan-friendly plans with good claim
          support and a wide garage network. Here are five that are often
          preferred by Nissan owners:
        </p>
        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🏥</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Bajaj Allianz
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Known for digital-friendly policies and wide cashless garage
              access.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              HDFC ERGO
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Offers strong claim settlement and hybrid add-on customizations.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Tata AIG
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Known for flexible coverage for urban users and new-age drivers.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📋</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              ICICI Lombard
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Fast claim processing and ideal for high-value models.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💰</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Future Generali
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Affordable pricing with high claim approval rates.
            </p>
          </div>
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                How to Save on Nissan Car Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              Want to reduce your premium without compromising on quality? Here
              are a few practical suggestions:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Compare quotes across insurers to find value.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Keep claim history clean to retain No Claim Bonus (NCB).
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Install anti-theft devices approved by ARAI for discounts.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Bundle insurance if you own multiple vehicles.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Renew on time to avoid penalties or inspections.</span>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-emerald-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required for Nissan Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              To purchase or renew insurance for your Nissan car, you’ll
              typically need:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Registration Certificate (RC)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Existing policy (if applicable)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Valid PUC certificate</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Aadhaar/PAN for verification</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl sm:col-span-2">
                <span>Recent passport-sized photo (offline only)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          Nissan Car Insurance FAQs
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <FaCarSide />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Nissan cars blend style, tech and value, making them a practical
                choice for Indian drivers. Securing the right insurance policy
                ensures you enjoy hassle-free ownership, whether you’re
                navigating city streets or heading out on a long drive.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                At TheFinMaster, we make it easy to compare plans, pick the
                right add-ons and get the best coverage tailored for your
                Nissan. From Magnite to Kicks, protect what drives you.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default NissanCarInsurance;
